import * as THREE from 'three';
import { WallType, WallConnectionType, WallEdgeConnection, LineType, WallConnectionStart, WallConnectionEnd, WallConnectionMiddle, VoidFillMetadata } from '../types/floorplannerTypes';
import type { FloorplannerStore } from '../store/floorplannerStore';
import { sharedJointStateManager, SharedJointState } from './SharedJointStateManager';
import { getConnectionPoint } from './connectionGeometry';

// Lazy accessor to avoid circular dependency with floorplannerStore
// During store initialization/rehydration, floorplannerStore may not be fully
// exported yet, so we defer access until it's actually needed at runtime.
let _floorplannerStore: FloorplannerStore | null = null;
const getFloorplannerStore = (): FloorplannerStore => {
  if (!_floorplannerStore) {
    const globalStore = (globalThis as typeof globalThis & { __boniFloorplannerStore?: FloorplannerStore }).__boniFloorplannerStore;
    if (globalStore) {
      _floorplannerStore = globalStore;
    } else {
      throw new Error('FloorplannerStore not yet initialized - JointDetector called too early');
    }
  }
  return _floorplannerStore;
};

/**
 * Represents a detected joint between multiple walls
 */
export interface JointInfo {
  /** Unique identifier for this joint */
  id: string;
  /** Geometric center point of the joint */
  point: THREE.Vector2;
  /** All walls connected at this joint */
  connectedWalls: WallConnectionInfo[];
  /** Type of joint based on number and arrangement of walls */
  jointType: JointType;
  /** Angles between consecutive walls (in radians) */
  angles: number[];
  /** Wall widths of connected walls */
  wallWidths: number[];
  /** Line styles of connected walls */
  lineStyles: LineType[];
  /** Fill colors of connected walls */
  fillColors: (number | undefined)[];
  /** Line colors of connected walls */
  lineColors: (number | undefined)[];
  /** Line weights of connected walls */
  lineWeights: (number | undefined)[];
  /** Whether this joint needs special handling */
  needsSpecialHandling: boolean;
  /** Calculated joint geometry for rendering */
  geometry?: JointGeometry;
  /** Reference to persistent joint state (for multi-wall joints) */
  persistentState?: SharedJointState;
  /** Optional metadata describing void-fill polygons for single-corner joints */
  voidFillMetadata?: VoidFillMetadata;
}

/**
 * Information about a wall's connection to a joint
 */
export interface WallConnectionInfo {
  /** Wall ID */
  wallId: string;
  /** Wall object reference */
  wall: WallType;
  /** Position on the wall where it connects to the joint (start/end/middle) */
  connectionPosition: 'start' | 'end' | 'middle';
  /** Actual geometric point used to group this wall into the joint */
  jointPoint?: THREE.Vector2;
  /** Angle of the wall at the joint (in radians) */
  angle: number;
  /** Direction vector of the wall at the joint */
  direction: THREE.Vector2;
  /** Whether this wall is the primary wall for joint calculations */
  isPrimary: boolean;
}

/**
 * Types of joints based on wall arrangement
 */
export type JointType = 'L' | 'T' | 'X' | 'MULTI' | 'SINGLE';

/**
 * Calculated geometry for joint rendering
 */
export interface JointGeometry {
  /** Center point of the joint */
  center: THREE.Vector2;
  /** Radius of the joint area */
  radius: number;
  /** Polygon defining the joint fill area */
  fillPolygon: THREE.Vector2[];
  /** Outline paths for the joint */
  outlinePaths: THREE.Vector2[][];
  /** Individual wall connection points */
  connectionPoints: Map<string, THREE.Vector2[]>;
}

/**
 * Main class for detecting and classifying wall joints
 */
export class JointDetector {
  private jointCache = new Map<string, JointInfo>();
  private localWallJointCache = new Map<string, JointInfo[]>();
  private connectionToJointMap = new Map<string, string>(); // Maps connection points to joint IDs
  private readonly ANGLE_TOLERANCE = Math.PI / 36; // 5 degrees in radians
  private lastDetectionTime = 0;
  private readonly DETECTION_DEBOUNCE = 16; // ~60fps limit for joint detection

  /**
   * Detect all joints in the current floorplan using topology-based approach.
   * Scans both centerConnections (legacy) and edgeConnections (modern) to find all joints.
   */
  detectAllJoints(): Map<string, JointInfo> {
    // Return cached results if available
    if (this.jointCache.size > 0) {
      return new Map(this.jointCache);
    }

    // Throttle detection during rapid operations
    const now = performance.now();
    if (now - this.lastDetectionTime < this.DETECTION_DEBOUNCE) {
      return new Map(this.jointCache);
    }
    this.lastDetectionTime = now;

    this.jointCache.clear();
    this.connectionToJointMap.clear();
    const processedJoints = new Set<string>();

    // Build joints from wall connections topology
    getFloorplannerStore().wallsMap.forEach((wall, wallId) => {
      // Process centerConnections (legacy system)
      if (wall.centerConnections) {
        wall.centerConnections.forEach(connection => {
          // Edge-mode center connections are compatibility mirrors created from
          // edgeConnections during load/save. The real edge topology is scanned
          // below; using the mirrors here can create approximate 2-wall joints
          // before the accurate multi-joint is detected.
          if (connection.mode === 'edge') {
            return;
          }

          const jointCandidates = this.gatherJointCandidatesFromConnection(wallId, connection);

          if (jointCandidates.length > 1) {
            const jointId = this.generateTopologyBasedJointId(jointCandidates);

            if (!processedJoints.has(jointId)) {
              const joint = this.createJointInfoFromTopology(jointCandidates);
              this.jointCache.set(joint.id, joint);
              processedJoints.add(jointId);

              // Map connection points to this joint
              jointCandidates.forEach(candidate => {
                const connectionKey = `${candidate.wallId}:${candidate.connectionPosition}`;
                this.connectionToJointMap.set(connectionKey, joint.id);
              });
            }
          }
        });
      }

      // Process edgeConnections (modern system)
      if (wall.edgeConnections) {
        wall.edgeConnections.forEach(edgeConnection => {
          const jointCandidates = this.gatherJointCandidatesFromEdgeConnection(wallId, edgeConnection);

          if (jointCandidates.length > 1) {
            const jointId = this.generateTopologyBasedJointId(jointCandidates);

            if (!processedJoints.has(jointId)) {
              const joint = this.createJointInfoFromTopology(jointCandidates);
              this.jointCache.set(joint.id, joint);
              processedJoints.add(jointId);

              // Map connection points to this joint
              jointCandidates.forEach(candidate => {
                const connectionKey = `${candidate.wallId}:${candidate.connectionPosition}`;
                this.connectionToJointMap.set(connectionKey, joint.id);
              });
            }
          }
        });
      }
    });

    return new Map(this.jointCache);
  }

  /**
   * Detect joint at a specific point
   */
  detectJointAtPoint(point: THREE.Vector2): JointInfo | null {
    // Search for walls at this point using topology
    const candidates: WallConnectionInfo[] = [];
    const tolerance = 0.001;

    getFloorplannerStore().wallsMap.forEach((wall, wallId) => {
      // Check if wall connects at this point
      if (wall.start.distanceTo(point) < tolerance) {
        candidates.push(this.createWallConnectionInfo(wall, wallId, 'start', point));
      } else if (wall.end.distanceTo(point) < tolerance) {
        candidates.push(this.createWallConnectionInfo(wall, wallId, 'end', point));
      }
    });

    if (candidates.length <= 1) return null;

    return this.createJointInfoFromTopology(candidates);
  }

  /**
   * Get joint information for a specific wall
   */
  getJointsForWall(wallId: string): JointInfo[] {
    const localJoints = this.localWallJointCache.get(wallId);
    if (localJoints) {
      return localJoints;
    }

    const joints = this.detectAllJoints();
    const wallJoints: JointInfo[] = [];

    joints.forEach(joint => {
      if (joint.connectedWalls.some(conn => conn.wallId === wallId)) {
        wallJoints.push(joint);
      }
    });

    return wallJoints;
  }

  /**
   * Detect joints for a bounded set of walls using existing topology links.
   *
   * Width/style changes do not alter topology, so callers can avoid the
   * floorplan-wide detectAllJoints path and refresh only the changed room's
   * connected joints.
   */
  getJointsForWallsLocal(wallIds: Iterable<string>): JointInfo[] {
    const store = getFloorplannerStore();
    const scopeWallIds = new Set<string>();

    const addWallAndDirectConnections = (wallId: string): void => {
      const wall = store.wallsMap.get(wallId);
      if (!wall) {
        return;
      }

      scopeWallIds.add(wallId);
      wall.centerConnections?.forEach(connection => {
        if (store.wallsMap.has(connection.id)) {
          scopeWallIds.add(connection.id);
        }
      });
      wall.edgeConnections?.forEach(connection => {
        if (store.wallsMap.has(connection.targetWallId)) {
          scopeWallIds.add(connection.targetWallId);
        }
      });
    };

    for (const wallId of wallIds) {
      addWallAndDirectConnections(wallId);
    }

    Array.from(scopeWallIds).forEach(addWallAndDirectConnections);

    const joints = new Map<string, JointInfo>();
    const storeCandidates = (candidates: WallConnectionInfo[]): void => {
      const uniqueCandidates = new Map<string, WallConnectionInfo>();
      candidates.forEach(candidate => {
        uniqueCandidates.set(`${candidate.wallId}:${candidate.connectionPosition}`, candidate);
      });

      if (uniqueCandidates.size <= 1) {
        return;
      }

      const joint = this.createJointInfoFromTopology(Array.from(uniqueCandidates.values()));
      joints.set(joint.id, joint);
    };

    scopeWallIds.forEach(wallId => {
      const wall = store.wallsMap.get(wallId);
      if (!wall) {
        return;
      }

      wall.centerConnections?.forEach(connection => {
        if (connection.mode === 'edge') {
          return;
        }
        storeCandidates(this.gatherJointCandidatesFromConnection(wallId, connection, scopeWallIds));
      });

      wall.edgeConnections?.forEach(connection => {
        storeCandidates(this.gatherJointCandidatesFromEdgeConnection(wallId, connection, scopeWallIds));
      });
    });

    const endpointTolerance = 0.001;
    scopeWallIds.forEach(wallId => {
      const wall = store.wallsMap.get(wallId);
      if (!wall) {
        return;
      }

      ([wall.start, wall.end] as const).forEach(point => {
        const candidates: WallConnectionInfo[] = [];
        scopeWallIds.forEach(otherWallId => {
          const otherWall = store.wallsMap.get(otherWallId);
          if (!otherWall) {
            return;
          }

          if (otherWall.start.distanceTo(point) < endpointTolerance) {
            candidates.push(this.createWallConnectionInfo(otherWall, otherWallId, 'start', point));
          } else if (otherWall.end.distanceTo(point) < endpointTolerance) {
            candidates.push(this.createWallConnectionInfo(otherWall, otherWallId, 'end', point));
          }
        });

        storeCandidates(candidates);
      });
    });

    scopeWallIds.forEach(wallId => {
      this.localWallJointCache.set(wallId, []);
    });

    joints.forEach(joint => {
      joint.connectedWalls.forEach(candidate => {
        if (!scopeWallIds.has(candidate.wallId)) {
          return;
        }

        const cachedJoints = this.localWallJointCache.get(candidate.wallId) ?? [];
        if (!cachedJoints.some(cachedJoint => cachedJoint.id === joint.id)) {
          cachedJoints.push(joint);
        }
        this.localWallJointCache.set(candidate.wallId, cachedJoints);
      });
    });

    return Array.from(joints.values());
  }

  /**
   * Check if a wall endpoint participates in a multi-joint (3+ walls).
   * Used by 2-wall systems to know when to "step aside" and let the multi-joint system handle rendering.
   * 
   * @param wallId - The wall ID to check
   * @param endpoint - Which endpoint to check ('start' or 'end')
   * @returns true if the endpoint is part of a joint with 3+ walls
   */
  isEndpointInMultiJoint(wallId: string, endpoint: 'start' | 'end'): boolean {
    const joints = this.getJointsForWall(wallId);
    return joints.some(joint => 
      joint.connectedWalls.length >= 3 &&
      joint.connectedWalls.some(cw => 
        cw.wallId === wallId && cw.connectionPosition === endpoint
      )
    );
  }

  /**
   * Get all wall IDs participating in a multi-joint at this endpoint.
   * Returns empty array if not a multi-joint (less than 3 walls).
   * 
   * @param wallId - The wall ID to check
   * @param endpoint - Which endpoint to check ('start' or 'end')
   * @returns Array of wall IDs participating in the multi-joint, or empty array
   */
  getMultiJointParticipants(wallId: string, endpoint: 'start' | 'end'): string[] {
    const joints = this.getJointsForWall(wallId);
    const multiJoint = joints.find(joint => 
      joint.connectedWalls.length >= 3 &&
      joint.connectedWalls.some(cw => 
        cw.wallId === wallId && cw.connectionPosition === endpoint
      )
    );
    return multiJoint?.connectedWalls.map(cw => cw.wallId) || [];
  }

  /**
   * Get joint information for a specific wall endpoint.
   * Returns the JointInfo if a joint exists at this endpoint, null otherwise.
   * 
   * @param wallId - The wall ID to check
   * @param endpoint - Which endpoint to check ('start' or 'end')
   * @returns JointInfo if found, null otherwise
   */
  getJointAtEndpoint(wallId: string, endpoint: 'start' | 'end'): JointInfo | null {
    const joints = this.getJointsForWall(wallId);
    return joints.find(joint => 
      joint.connectedWalls.some(cw => 
        cw.wallId === wallId && cw.connectionPosition === endpoint
      )
    ) || null;
  }

  /**
   * Count the number of walls at a given endpoint.
   * Used to determine if adding another wall would create a multi-joint.
   * 
   * @param wallId - The wall ID to check
   * @param endpoint - Which endpoint to check ('start' or 'end')
   * @returns Number of walls at this endpoint (including the wall itself)
   */
  countWallsAtEndpoint(wallId: string, endpoint: 'start' | 'end'): number {
    const joint = this.getJointAtEndpoint(wallId, endpoint);
    return joint?.connectedWalls.length || 1; // At least the wall itself
  }

  /**
   * Count walls at an endpoint directly from wall data (bypasses cache).
   * Useful during snapping operations when the joint cache may be stale.
   * 
   * @param wallId - The wall ID to check
   * @param endpoint - Which endpoint to check ('start' or 'end')
   * @param excludeWallId - Optional wall ID to exclude from count (e.g., the wall being snapped)
   * @returns Number of walls at this endpoint
   */
  countWallsAtEndpointDirect(wallId: string, endpoint: 'start' | 'end', excludeWallId?: string): number {
    const wall = getFloorplannerStore().wallsMap.get(wallId);
    if (!wall) return 0;

    const endpointPosition = endpoint === 'start' ? wall.start : wall.end;
    // Use a tolerance based on wall width to account for edge-based connections
    // where walls can connect to different corners of the same endpoint.
    // The corners can be up to wallWidth apart (diagonal = wallWidth * sqrt(2) ≈ 1.5 * wallWidth)
    const maxWallWidth = getFloorplannerStore().wallWidth * 1.5;
    const tolerance = maxWallWidth;
    const wallIds = new Set<string>([wallId]);

    // Check all walls for endpoint proximity
    getFloorplannerStore().wallsMap.forEach((otherWall, otherWallId) => {
      if (otherWallId === wallId || otherWallId === excludeWallId) return;

      if (otherWall.start.distanceTo(endpointPosition) < tolerance) {
        wallIds.add(otherWallId);
      } else if (otherWall.end.distanceTo(endpointPosition) < tolerance) {
        wallIds.add(otherWallId);
      }
    });

    return wallIds.size;
  }

  /**
   * Check if an endpoint would become a multi-joint if a new wall connects there.
   * Used during snapping to decide whether to use 2-wall or multi-joint system.
   * 
   * @param targetWallId - The wall being connected to
   * @param targetEndpoint - Which endpoint is being connected to
   * @param sourceWallId - The wall being connected (to exclude from count)
   * @returns true if connecting would create a 3+ wall joint
   */
  wouldCreateMultiJoint(
    targetWallId: string, 
    targetEndpoint: 'start' | 'end', 
    sourceWallId: string
  ): boolean {
    // Count existing walls at the target endpoint (excluding the source wall)
    const existingCount = this.countWallsAtEndpointDirect(targetWallId, targetEndpoint, sourceWallId);
    // If there are already 2+ walls there, adding another creates a multi-joint
    return existingCount >= 2;
  }

  /**
   * Gather all walls that participate in a joint from a centerConnection using topology
   */
  private gatherJointCandidatesFromConnection(
    wallId: string,
    connection: WallConnectionType,
    searchWallIds?: Set<string>
  ): WallConnectionInfo[] {
    const candidates: WallConnectionInfo[] = [];
    const wall = getFloorplannerStore().wallsMap.get(wallId);
    const connectedWall = getFloorplannerStore().wallsMap.get(connection.id);

    if (!wall || !connectedWall) return candidates;

    // Add the current wall
    const sourcePosition = this.mapPositionToString(connection.sourcePosition);
    const sourcePoint = this.getWallPointAtPosition(wall, connection.sourcePosition);
    if (sourcePoint) {
      candidates.push(this.createWallConnectionInfo(wall, wallId, sourcePosition, sourcePoint));
    }

    // Add the connected wall
    const targetPosition = this.mapPositionToString(connection.targetPosition);
    const targetPoint = this.getWallPointAtPosition(connectedWall, connection.targetPosition);
    if (targetPoint) {
      candidates.push(this.createWallConnectionInfo(connectedWall, connection.id, targetPosition, targetPoint));
    }

    // Find other walls that connect at the same point through topology
    const jointPoint = sourcePoint || targetPoint;
    if (jointPoint) {
      this.findOtherConnectedWalls(jointPoint, [wallId, connection.id], candidates, searchWallIds);
    }

    return candidates;
  }

  /**
   * Gather all walls that participate in a joint from an edgeConnection.
   * This method discovers multi-wall joints where 3+ walls connect at the same endpoint
   * using edge connections (modern system).
   * 
   * IMPORTANT: Walls can connect to different CORNERS of the same target endpoint.
   * For example, two walls might both connect to a wall's "start" endpoint but at
   * different corners (top vs bottom). These should be part of the same multi-joint.
   */
  private gatherJointCandidatesFromEdgeConnection(
    wallId: string,
    edgeConnection: WallEdgeConnection,
    searchWallIds?: Set<string>
  ): WallConnectionInfo[] {
    const candidates: WallConnectionInfo[] = [];
    const wall = getFloorplannerStore().wallsMap.get(wallId);
    const connectedWall = getFloorplannerStore().wallsMap.get(edgeConnection.targetWallId);

    if (!wall || !connectedWall) return candidates;

    // Add the source wall (the wall that has this edge connection)
    const sourceEndpoint = edgeConnection.sourceEndpoint;
    const sourcePoint = sourceEndpoint === 'start' ? wall.start : wall.end;
    candidates.push(this.createWallConnectionInfo(wall, wallId, sourceEndpoint, sourcePoint));

    // Add the target wall
    // For edge connections, targetEndpoint can be 'start', 'end', or 'middle'
    let targetEndpoint = edgeConnection.targetEndpoint;
    let targetPoint: THREE.Vector2;
    if (targetEndpoint === 'start') {
      targetPoint = connectedWall.start;
    } else if (targetEndpoint === 'end') {
      targetPoint = connectedWall.end;
    } else {
      // 'middle' - use the target edge point as approximation for joint center
      // targetEdgePoint is typed as THREE.Vector2 in WallEdgeConnection interface
      targetPoint = edgeConnection.targetEdgePoint.clone();
      const endpointTolerance = (connectedWall.wallWidth || getFloorplannerStore().wallWidth) + 1e-6;
      const distToStart = targetPoint.distanceTo(connectedWall.start);
      const distToEnd = targetPoint.distanceTo(connectedWall.end);
      if (distToStart <= endpointTolerance || distToEnd <= endpointTolerance) {
        targetEndpoint = distToStart <= distToEnd ? 'start' : 'end';
        targetPoint = targetEndpoint === 'start' ? connectedWall.start : connectedWall.end;
      }
    }
    candidates.push(this.createWallConnectionInfo(connectedWall, edgeConnection.targetWallId, targetEndpoint, targetPoint));

    // For endpoint-to-endpoint connections, use the TARGET endpoint as the joint center
    // This is important because multiple walls may connect to different CORNERS of
    // the same target endpoint (e.g., top corner vs bottom corner of the same wall end)
    const jointPoint = (targetEndpoint === 'start' || targetEndpoint === 'end') 
      ? targetPoint 
      : sourcePoint;

    // Find other walls that connect at the same point (using position-based search)
    this.findOtherConnectedWalls(jointPoint, [wallId, edgeConnection.targetWallId], candidates, searchWallIds);

    // CRITICAL: Also find walls that connect to the SAME target endpoint via edge connections
    // This handles the case where multiple walls connect to different corners of the same endpoint
    if (targetEndpoint === 'start' || targetEndpoint === 'end') {
      this.findWallsConnectedToSameEndpoint(
        edgeConnection.targetWallId, 
        targetEndpoint, 
        [wallId, edgeConnection.targetWallId], 
        candidates,
        searchWallIds
      );
    }

    return candidates;
  }

  /**
   * Find all walls that have edge connections to the same (targetWallId, targetEndpoint).
   * This is crucial for detecting multi-joints where walls connect to different CORNERS
   * of the same endpoint (e.g., one wall connects to top corner, another to bottom corner).
   */
  private findWallsConnectedToSameEndpoint(
    targetWallId: string,
    targetEndpoint: 'start' | 'end',
    excludeWallIds: string[],
    candidates: WallConnectionInfo[],
    searchWallIds?: Set<string>
  ): void {
    const targetWall = getFloorplannerStore().wallsMap.get(targetWallId);
    if (!targetWall) return;

    // The joint point is the target wall's endpoint centerline position
    const jointPoint = targetEndpoint === 'start' ? targetWall.start : targetWall.end;

    const inspectWall = (wall: WallType, wallId: string): void => {
      if (excludeWallIds.includes(wallId)) return;
      
      // Check if this wall already exists in candidates
      const existing = candidates.find(c => c.wallId === wallId);
      if (existing) return;

      // Check if this wall has an edge connection to the same target endpoint
      if (wall.edgeConnections) {
        for (const edgeConn of wall.edgeConnections) {
          if (edgeConn.targetWallId === targetWallId && edgeConn.targetEndpoint === targetEndpoint) {
            // This wall connects to the same target endpoint - add it to candidates
            candidates.push(this.createWallConnectionInfo(
              wall, 
              wallId, 
              edgeConn.sourceEndpoint, 
              jointPoint
            ));
            return; // Found match, no need to continue checking this wall
          }
        }
      }
    };

    if (searchWallIds) {
      searchWallIds.forEach(wallId => {
        const wall = getFloorplannerStore().wallsMap.get(wallId);
        if (wall) {
          inspectWall(wall, wallId);
        }
      });
      return;
    }

    // Search all walls for edge connections that target the same (targetWallId, targetEndpoint)
    getFloorplannerStore().wallsMap.forEach(inspectWall);
  }

  /**
   * Create wall connection info for a joint
   */
  private createWallConnectionInfo(
    wall: WallType,
    wallId: string,
    connectionPosition: 'start' | 'end' | 'middle',
    jointPoint: THREE.Vector2
  ): WallConnectionInfo {
    const direction = this.getWallDirectionAtPoint(wall, connectionPosition);
    const angle = Math.atan2(direction.y, direction.x);

    return {
      wallId,
      wall,
      connectionPosition,
      jointPoint: jointPoint.clone(),
      angle,
      direction,
      isPrimary: false // Will be set later
    };
  }

  /**
   * Get wall direction vector at a specific connection point
   */
  private getWallDirectionAtPoint(wall: WallType, connectionPosition: 'start' | 'end' | 'middle'): THREE.Vector2 {
    const direction = wall.end.clone().sub(wall.start).normalize();

    // If connecting at start, direction points away from start
    // If connecting at end, direction points toward end
    // If connecting at middle, use the wall direction
    switch (connectionPosition) {
      case 'start':
        return direction;
      case 'end':
        return direction.negate();
      case 'middle':
        return direction; // For T-joints, we'll handle this in classification
      default:
        return direction;
    }
  }

  /**
   * Create joint information from topology-based wall candidates
   */
  private createJointInfoFromTopology(wallCandidates: WallConnectionInfo[]): JointInfo {
    // Calculate joint center point
    const jointPoint = this.calculateJointCenterPoint(wallCandidates);

    // Sort walls by angle for consistent processing
    const sortedWalls = [...wallCandidates].sort((a, b) => a.angle - b.angle);

    const jointType = this.classifyJointWithAngles(sortedWalls);
    const wallIds = sortedWalls.map(w => w.wallId);

    // Register or get persistent joint state for multi-wall joints
    let persistentState: SharedJointState | null = null;
    if (wallIds.length >= 3) {
      // Check if we're in a drag operation
      const isDragOperation = getFloorplannerStore().isDragInProgress ? getFloorplannerStore().isDragInProgress() : false;

      persistentState = sharedJointStateManager.registerMultiJoint(
        wallIds,
        jointPoint,
        isDragOperation
      );
    }

    // Use persistent state for primary wall election if available
    if (persistentState) {
      // Mark the persistent primary wall
      const primaryWallId = persistentState.primaryWallId;
      sortedWalls.forEach(wall => {
        wall.isPrimary = wall.wallId === primaryWallId;
      });
    } else {
      // Fallback to first wall as primary for non-multi joints
      if (sortedWalls.length > 0) {
        sortedWalls[0].isPrimary = true;
      }
    }

    const angles = this.calculateAngles(sortedWalls);
    const wallWidths = sortedWalls.map(conn => conn.wall.wallWidth || getFloorplannerStore().wallWidth);
    const lineStyles = sortedWalls.map(conn => conn.wall.lineType as LineType || LineType.solid);
    const fillColors = sortedWalls.map(conn => conn.wall.fillColor || getFloorplannerStore().fillColor);
    const lineColors = sortedWalls.map(conn => conn.wall.lineColor || getFloorplannerStore().lineColor);
    const lineWeights = sortedWalls.map(conn => conn.wall.lineWeight || getFloorplannerStore().wallLineWeight);

    const voidFillMetadata = this.extractVoidFillMetadata(sortedWalls);
    const joint: JointInfo = {
      id: persistentState ? persistentState.id : this.generateTopologyBasedJointId(sortedWalls),
      point: jointPoint,
      connectedWalls: sortedWalls,
      jointType,
      angles,
      wallWidths,
      lineStyles,
      fillColors,
      lineColors,
      lineWeights,
      needsSpecialHandling: !!voidFillMetadata || this.needsSpecialHandling(jointType, wallWidths, lineStyles),
      geometry: this.calculateJointGeometry(jointPoint, sortedWalls, wallWidths),
      persistentState: persistentState || undefined,
      voidFillMetadata: voidFillMetadata || undefined,
    };

    return joint;
  }

  /**
   * Extract void fill metadata for joints that are formed from single-corner connections.
   * The metadata is stored on edge connections and shared between participating walls.
   */
  private extractVoidFillMetadata(walls: WallConnectionInfo[]): VoidFillMetadata | undefined {
    if (walls.length < 2) {
      return undefined;
    }

    for (const wallInfo of walls) {
      const edgeConnections = wallInfo.wall.edgeConnections || [];
      const matchingConnection = edgeConnections.find(conn =>
        conn.voidFill?.required &&
        conn.sourceWallId === wallInfo.wallId &&
        conn.targetWallId !== wallInfo.wallId && // safety guard
        walls.some(other => other.wallId === conn.targetWallId)
      );

      if (matchingConnection?.voidFill) {
        return matchingConnection.voidFill;
      }
    }

    return undefined;
  }

  /**
   * Classify joint type based on angles between connected walls
   */
  private classifyJointWithAngles(walls: WallConnectionInfo[]): JointType {
    const count = walls.length;

    if (count === 1) return 'SINGLE';

    if (count === 2) {
      // Check if walls are collinear (straight pass-through) or form an L
      const angleDiff = Math.abs(walls[0].angle - walls[1].angle);
      const normalizedAngle = Math.min(angleDiff, 2 * Math.PI - angleDiff);

      // If angle is close to π, walls are collinear (straight connection)
      if (Math.abs(normalizedAngle - Math.PI) < this.ANGLE_TOLERANCE) {
        return 'SINGLE'; // Treat collinear as single for rendering
      }
      if (walls.some(wall => wall.connectionPosition === 'middle')) {
        return 'T';
      }
      return 'L';
    }

    if (count === 3) {
      // Check if it's a true T-joint (one pair collinear, third branching)
      return this.isValidTJoint(walls) ? 'T' : 'MULTI';
    }

    if (count === 4) {
      // Check if it's a true X-joint (two pairs of opposing collinear walls)
      return this.isValidXJoint(walls) ? 'X' : 'MULTI';
    }

    return 'MULTI';
  }

  /**
   * Calculate angles between consecutive walls
   */
  private calculateAngles(walls: WallConnectionInfo[]): number[] {
    if (walls.length < 2) return [];

    const angles: number[] = [];

    for (let i = 0; i < walls.length; i++) {
      const current = walls[i];
      const next = walls[(i + 1) % walls.length];

      let angleDiff = next.angle - current.angle;

      // Normalize angle to [0, 2π]
      while (angleDiff < 0) angleDiff += 2 * Math.PI;
      while (angleDiff > 2 * Math.PI) angleDiff -= 2 * Math.PI;

      angles.push(angleDiff);
    }

    return angles;
  }

  /**
   * Determine if joint needs special handling
   */
  private needsSpecialHandling(
    jointType: JointType,
    wallWidths: number[],
    lineStyles: LineType[]
  ): boolean {
    // Special handling (joint polygons) needed ONLY for:
    // 1. MULTI joints (complex arrangements with 3+ walls)
    // 2. Different wall widths (any joint type)
    // 3. Different line styles (any joint type)
    //
    // Simple L, T, and X joints with uniform widths/styles use trimming instead

    if (jointType === 'MULTI') {
      return true;
    }

    // Check for different wall widths
    const firstWidth = wallWidths[0];
    if (wallWidths.some(width => Math.abs(width - firstWidth) > 0.001)) {
      return true;
    }

    // Check for different line styles
    const firstStyle = lineStyles[0];
    if (lineStyles.some(style => style !== firstStyle)) {
      return true;
    }

    return false;
  }

  /**
   * Calculate joint geometry for rendering
   */
  private calculateJointGeometry(
    center: THREE.Vector2,
    walls: WallConnectionInfo[],
    wallWidths: number[]
  ): JointGeometry {
    const maxWidth = Math.max(...wallWidths);
    const radius = maxWidth / 2;

    // Create a simple circular fill polygon for now
    // This will be enhanced in subsequent phases
    const fillPolygon: THREE.Vector2[] = [];
    const segments = 16;

    for (let i = 0; i < segments; i++) {
      const angle = (i / segments) * 2 * Math.PI;
      const point = new THREE.Vector2(
        center.x + Math.cos(angle) * radius,
        center.y + Math.sin(angle) * radius
      );
      fillPolygon.push(point);
    }

    // Calculate connection points for each wall
    const connectionPoints = new Map<string, THREE.Vector2[]>();

    walls.forEach(wallConn => {
      const wallRadius = (wallConn.wall.wallWidth || getFloorplannerStore().wallWidth) / 2;
      const perpendicular = new THREE.Vector2(-wallConn.direction.y, wallConn.direction.x);

      const points = [
        center.clone().add(perpendicular.clone().multiplyScalar(wallRadius)),
        center.clone().add(perpendicular.clone().multiplyScalar(-wallRadius))
      ];

      connectionPoints.set(wallConn.wallId, points);
    });

    return {
      center: center.clone(),
      radius,
      fillPolygon,
      outlinePaths: [fillPolygon], // Simple outline for now
      connectionPoints
    };
  }

  /**
   * Generate topology-based joint ID using wall IDs and connection metadata
   */
  private generateTopologyBasedJointId(walls: WallConnectionInfo[]): string {
    // Create hash from sorted wall IDs and their connection positions
    const wallData = walls
      .map(w => `${w.wallId}:${w.connectionPosition}`)
      .sort()
      .join('|');

    // Use a simple hash of the wall data
    let hash = 0;
    for (let i = 0; i < wallData.length; i++) {
      const char = wallData.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    return `joint_topo_${Math.abs(hash).toString(36)}`;
  }

  /**
   * Generate point key for caching
   */
  private getPointKey(point: THREE.Vector2, precision: number = 3): string {
    return `${point.x.toFixed(precision)},${point.y.toFixed(precision)}`;
  }

  /**
   * Invalidate joint cache
   */
  invalidateCache(): void {
    this.jointCache.clear();
    this.localWallJointCache.clear();
    this.connectionToJointMap.clear();
    this.lastDetectionTime = 0;
    // Only clear SharedJointStateManager on explicit reset, not on routine cache invalidation
    // The SharedJointStateManager manages its own lifecycle and should persist across cache clears
  }

  /**
   * Full reset including persistent joint state (for floorplan reset only)
   */
  fullReset(): void {
    this.invalidateCache();
    sharedJointStateManager.clearAll();
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; connectionMappings: number } {
    return {
      size: this.jointCache.size,
      connectionMappings: this.connectionToJointMap.size
    };
  }

  /**
   * Map numeric position to string position
   */
  private mapPositionToString(position: number): 'start' | 'end' | 'middle' {
    if (position === WallConnectionStart) return 'start';
    if (position === WallConnectionEnd) return 'end';
    if (position === WallConnectionMiddle) return 'middle';
    // Default to start for unknown positions
    return 'start';
  }

  /**
   * Get wall point at specific position
   */
  private getWallPointAtPosition(wall: WallType, position: number): THREE.Vector2 | null {
    if (!wall.start || !wall.end) {
      return null;
    }
    return getConnectionPoint(wall.start, wall.end, position);
  }

  /**
   * Find other walls that connect at the same point through topology.
   * Scans both centerConnections and edgeConnections to find all walls at a joint.
   * 
   * Uses two tolerance levels:
   * - Strict tolerance (0.001) for exact centerline matches
   * - Width-based tolerance for edge connections where walls may connect to different corners
   */
  private findOtherConnectedWalls(
    jointPoint: THREE.Vector2,
    excludeWallIds: string[],
    candidates: WallConnectionInfo[],
    searchWallIds?: Set<string>
  ): void {
    const strictTolerance = 0.001;
    // Use a larger tolerance that accounts for walls connecting to different corners
    // This is based on the maximum expected wall width (typically up to 0.5 units)
    const widthBasedTolerance = getFloorplannerStore().wallWidth * 1.5;

    const inspectWall = (wall: WallType, wallId: string): void => {
      if (excludeWallIds.includes(wallId)) return;

      // Check if this wall already exists in candidates
      const existing = candidates.find(c => c.wallId === wallId);
      if (existing) return;

      // Check if wall endpoint is at the joint point (direct geometry check)
      // Use strict tolerance for centerline matching
      if (wall.start.distanceTo(jointPoint) < strictTolerance) {
        candidates.push(this.createWallConnectionInfo(wall, wallId, 'start', jointPoint));
        return;
      }
      if (wall.end.distanceTo(jointPoint) < strictTolerance) {
        candidates.push(this.createWallConnectionInfo(wall, wallId, 'end', jointPoint));
        return;
      }

      // Check centerConnections (legacy system)
      if (wall.centerConnections) {
        for (const connection of wall.centerConnections) {
          const connectionPoint = this.getWallPointAtPosition(wall, connection.sourcePosition);
          if (connectionPoint && connectionPoint.distanceTo(jointPoint) < strictTolerance) {
            const position = this.mapPositionToString(connection.sourcePosition);
            candidates.push(this.createWallConnectionInfo(wall, wallId, position, jointPoint));
            return; // Found match, no need to continue
          }
        }
      }

      // Check edgeConnections (modern system)
      // Use width-based tolerance to account for walls connecting to different corners
      if (wall.edgeConnections) {
        for (const edgeConn of wall.edgeConnections) {
          // Check source endpoint with width-based tolerance
          const sourcePoint = edgeConn.sourceEndpoint === 'start' ? wall.start : wall.end;
          if (sourcePoint.distanceTo(jointPoint) < widthBasedTolerance) {
            candidates.push(this.createWallConnectionInfo(wall, wallId, edgeConn.sourceEndpoint, jointPoint));
            return; // Found match, no need to continue
          }
        }
      }
    };

    if (searchWallIds) {
      searchWallIds.forEach(wallId => {
        const wall = getFloorplannerStore().wallsMap.get(wallId);
        if (wall) {
          inspectWall(wall, wallId);
        }
      });
      return;
    }

    getFloorplannerStore().wallsMap.forEach(inspectWall);
  }

  /**
   * Calculate joint center point from wall candidates
   */
  private calculateJointCenterPoint(candidates: WallConnectionInfo[]): THREE.Vector2 {
    if (candidates.length === 0) return new THREE.Vector2();

    const center = new THREE.Vector2();
    candidates.forEach(candidate => {
      const point = candidate.jointPoint ?? this.getWallPointAtPosition(candidate.wall,
          candidate.connectionPosition === 'start' ? WallConnectionStart :
          candidate.connectionPosition === 'end' ? WallConnectionEnd : WallConnectionMiddle
        );
      if (point) {
        center.add(point);
      }
    });

    return center.divideScalar(candidates.length);
  }

  /**
   * Check if three walls form a valid T-joint
   */
  private isValidTJoint(walls: WallConnectionInfo[]): boolean {
    if (walls.length !== 3) return false;

    // Check if one pair of walls is collinear and the third branches
    for (let i = 0; i < walls.length; i++) {
      for (let j = i + 1; j < walls.length; j++) {
        const angleDiff = Math.abs(walls[i].angle - walls[j].angle);
        const normalizedAngle = Math.min(angleDiff, 2 * Math.PI - angleDiff);

        // If this pair is collinear, check if third wall branches
        if (Math.abs(normalizedAngle - Math.PI) < this.ANGLE_TOLERANCE) {
          return true; // Found collinear pair, this is a valid T-joint
        }
      }
    }

    return false;
  }

  /**
   * Check if four walls form a valid X-joint
   */
  private isValidXJoint(walls: WallConnectionInfo[]): boolean {
    if (walls.length !== 4) return false;

    // Check if we have two pairs of opposing collinear walls
    const angles = walls.map(w => w.angle).sort();
    let collinearPairs = 0;

    for (let i = 0; i < angles.length; i++) {
      for (let j = i + 1; j < angles.length; j++) {
        const angleDiff = Math.abs(angles[i] - angles[j]);
        const normalizedAngle = Math.min(angleDiff, 2 * Math.PI - angleDiff);

        if (Math.abs(normalizedAngle - Math.PI) < this.ANGLE_TOLERANCE) {
          collinearPairs++;
        }
      }
    }

    // For a valid X-joint, we should have exactly 2 collinear pairs
    return collinearPairs === 2;
  }
}

// Export singleton instance
export const jointDetector = new JointDetector();
